let cachedToken: string | null = null;

/**
 * Set the token directly in the in-memory store.
 * Called by AuthInitializer after getAccessTokenSilently() — required when
 * Auth0 uses memory cache (incognito mode) instead of localStorage.
 */
export const setMemoryToken = (token: string | null): void => {
  cachedToken = token;
};

const isJwtExpired = (token: string): boolean => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(decodeURIComponent(
      window.atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    ));
    return Math.floor(Date.now() / 1000) >= exp;
  } catch {
    return true;
  }
};

export const getAuth0Token = (): string | null => {
  try {
    // If we have a cached token and it's not expired, use it
    if (cachedToken) {
      if (!isJwtExpired(cachedToken)) return cachedToken;
      cachedToken = null; // Clear expired token
    }

    // Try to get the token from localStorage
    const auth0Cache = localStorage.getItem('@@auth0spajs@@::@@');
    if (!auth0Cache) return null;

    // Parse the Auth0 cache
    const parsedCache = JSON.parse(auth0Cache);
    const cacheKeys = Object.keys(parsedCache);

    // Find the key that contains the token
    for (const key of cacheKeys) {
      if (key.includes('@@')) {
        const authData = parsedCache[key];
        if (authData?.body?.access_token) {
          cachedToken = authData.body.access_token;
          return cachedToken;
        }
      }
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Clear the token cache
 */
export const clearTokenCache = (): void => {
  cachedToken = null;
};

/**
 * Check if the token is about to expire (within 5 minutes)
 * @returns {boolean} Whether the token is about to expire
 */
export const isTokenAboutToExpire = (): boolean => {
  try {
    const token = getAuth0Token();
    if (!token) return true;

    // Decode token to check expiration
    // This is a simple check without library dependencies
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const { exp } = JSON.parse(jsonPayload);
    const currentTime = Math.floor(Date.now() / 1000);
    const fiveMinutesInSeconds = 5 * 60;

    // Token is about to expire if it expires within 5 minutes
    return exp - currentTime < fiveMinutesInSeconds;
  } catch {
    return true; 
  }
};

export default {
  getAuth0Token,
  setMemoryToken,
  clearTokenCache,
  isTokenAboutToExpire
};